import { useCallback, useMemo, useState, useRef, useEffect } from 'react'
import { IConfigGroup, IConfigStateGroups } from 'constants/interfaces'
import { FixedSizeList as List } from 'react-window'
import { SelectionMode } from './useSelection'
export interface FlatNode extends IConfigGroup {
  path: string[]
  level: number
  hasChildren: boolean
  children: FlatNode[]
  descendantCount: number
}

/**
 * Custom hook for managing tree structure with optimized rendering
 * @param groups - The groups data from state
 * @param allowedGroups - Array of allowed group IDs
 * @param selectionMode - Selection mode
 * @param isCloseMode - Close mode
 * @returns Object with tree state and methods
 */
export const useFlatTree = (
  groups: IConfigStateGroups,
  allowedGroups?: string[],
  selectionMode?: SelectionMode,
  isCloseMode?: boolean
) => {
  // Set of open node IDs
  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set())
  // Search term for filtering nodes
  const [searchTerm, setSearchTerm] = useState('')
  // Search results
  const [searchResult, setSearchResult] = useState<FlatNode[]>([])

  // Reference to the virtualized list
  const listRef = useRef<List>(null)

  /**
   * Build the tree structure from flat groups data
   */
  const { fullTree } = useMemo(() => {
    if (!allowedGroups?.length) {
      return { fullTree: [] }
    }

    const allowedSet = new Set(allowedGroups)
    const map = new Map<string, FlatNode>()

    // Step 1: Create a map with all nodes
    Object.values(groups).forEach((group) => {
      if (allowedSet.has(group.oid)) {
        map.set(group.oid, {
          ...group,
          path: [],
          level: 0,
          hasChildren: group.child.length > 0,
          children: [],
          descendantCount: 0,
        })
      }
    })

    // Step 2: Connect parents with children
    map.forEach((node) => {
      if (node.parent && map.has(node.parent)) {
        const parent = map.get(node.parent)!
        parent.children.push(node)
      }
    })

    // Step 3: Recursively set levels and paths
    const setLevelsAndPaths = (node: FlatNode, level: number, parentPath: string[]) => {
      node.level = level
      node.path = [...parentPath, node.name]
      node.children.forEach((child) => setLevelsAndPaths(child, level + 1, node.path))
      node.hasChildren = node.children.length > 0
      node.descendantCount = node.children.reduce((count, child) => count + 1 + child.descendantCount, 0)
    }

    const roots = Array.from(map.values()).filter((node) => !node.parent || !map.has(node.parent))
    roots.forEach((root) => setLevelsAndPaths(root, 0, []))

    return { fullTree: roots }
  }, [groups, allowedGroups])

  /**
   * Get visible nodes based on which nodes are open
   */
  const getVisibleNodes = useCallback((nodes: FlatNode[], openNodes: Set<string>): FlatNode[] => {
    const result: FlatNode[] = []
    nodes.forEach((node) => {
      result.push(node)
      if (openNodes.has(node.oid)) {
        result.push(...getVisibleNodes(node.children, openNodes))
      }
    })
    return result
  }, [])

  /**
   * Calculate visible nodes based on open state
   */
  const visibleNodes = useMemo(() => getVisibleNodes(fullTree, openNodes), [fullTree, openNodes, getVisibleNodes])

  /**
   * Toggle node open/closed state
   */
  const toggleNode = (oid: string) => {
    setOpenNodes((prev) => {
      const next = new Set(prev)
      next.has(oid) ? next.delete(oid) : next.add(oid)
      return next
    })

    const index = visibleNodes.findIndex((node) => node.oid === oid)
    if (!openNodes.has(oid) && index !== -1) {
      setTimeout(() => listRef.current?.scrollToItem(index, 'start'), 0)
    }
  }

  /**
   * Handle search input change
   */
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)

    if (!e.target.value.trim()) {
      setSearchResult([])
      return
    }

    const foundNodes = searchNodes(fullTree, e.target.value)
    setSearchResult(foundNodes)
  }

  /**
   * Search for nodes matching the search term
   */
  const searchNodes = (tree: FlatNode[], searchTerm: string): FlatNode[] => {
    const result: FlatNode[] = []

    const checkNode = (node: FlatNode) => {
      if (node.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        result.push(node)
      }
      node.children.forEach(checkNode)
    }

    tree.forEach(checkNode)

    return result
  }

  /**
   * Calculate indeterminate state for checkboxes
   */
  const calculateIndeterminateState = (node: FlatNode, selectedIds: Set<string>): boolean => {
    if (node.children.length === 0) {
      return false
    }

    let hasSelectedChild = false
    let hasUnselectedChild = false

    for (const child of node.children) {
      if (selectedIds.has(child.oid)) {
        hasSelectedChild = true
      } else if (child.children.length > 0 && calculateIndeterminateState(child, selectedIds)) {
        hasSelectedChild = true
      } else {
        hasUnselectedChild = true
      }
    }

    return hasSelectedChild && (hasUnselectedChild || !selectedIds.has(node.oid))
  }

  /**
   * Get all children IDs for a node
   */
  const getChildrenIds = useCallback(
    (nodeId: string) => {
      const result: string[] = []

      const collectChildrenIds = (nodes: FlatNode[]) => {
        for (const node of nodes) {
          if (node.oid === nodeId) {
            // Found the node, collect all child IDs
            const collectIds = (children: FlatNode[]) => {
              for (const child of children) {
                result.push(child.oid)
                if (child.children.length > 0) {
                  collectIds(child.children)
                }
              }
            }

            collectIds(node.children)
            return true
          }

          // Recursively search for the node among children
          if (node.children.length > 0 && collectChildrenIds(node.children)) {
            return true
          }
        }

        return false
      }

      collectChildrenIds(fullTree)
      return result
    },
    [fullTree]
  )

  /**
   * Clear search term and results
   */
  const clearSearch = useCallback(() => {
    setSearchTerm('')
    setSearchResult([])
  }, [])

  /**
   * Expand tree to show a specific node
   */
  const expandToNode = useCallback(
    (nodeId: string) => {
      // Create a new Set for open nodes
      const newOpenNodes = new Set(openNodes)

      // Function to find path to node
      const findPathToNode = (nodes: FlatNode[]): string[] | null => {
        for (const node of nodes) {
          if (node.oid === nodeId) {
            return [node.oid]
          }

          if (node.children.length > 0) {
            const childPath = findPathToNode(node.children)
            if (childPath) {
              return [node.oid, ...childPath]
            }
          }
        }

        return null
      }

      // Find path to node
      const path = findPathToNode(fullTree)

      // If path found, open all nodes on the path (except the last one)
      if (path) {
        // Open all parent nodes (except the last one, which is the target)
        path.slice(0, -1).forEach((id) => {
          newOpenNodes.add(id)
        })

        setOpenNodes(newOpenNodes)
      }
    },
    [fullTree, openNodes]
  )

  /**
   * Initialize open nodes on component mount
   */
  useEffect(() => {
    const initialOpenNodes = new Set<string>()

    const openAllNodes = (nodes: FlatNode[]) => {
      nodes.forEach((node) => {
        initialOpenNodes.add(node.oid)
        if (node.children.length > 0) {
          openAllNodes(node.children)
        }
      })
    }

    const openLevelOneNodes = (nodes: FlatNode[]) => {
      nodes.forEach((node) => {
        if (node.level === 0 || node.level === 1) {
          initialOpenNodes.add(node.oid)
        }
      })
    }

    if (selectionMode === SelectionMode.RAW) {
      openAllNodes(fullTree)
    } else if (!isCloseMode) {
      return
    } else {
      openLevelOneNodes(fullTree)
    }

    setOpenNodes(initialOpenNodes)
  }, [fullTree, selectionMode, isCloseMode])

  return {
    fullTree,
    visibleNodes,
    toggleNode,
    openNodes,
    listRef,
    searchTerm,
    searchResult,
    handleSearch,
    calculateIndeterminateState,
    getChildrenIds,
    expandToNode,
    clearSearch,
  }
}
